import React, { useState } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import AdvertsDashboard from "../../adverts";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_ADVERTS_BAR_SHELL_160,
  SUITE_ADVERTS_TREND_PLACEHOLDER_220,
  ADVERTS_PLACEMENT_DEMO_BAR_STYLES
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import {
  AsyncStateBoundary,
  SuitePageShell,
  KpiStatCards
} from "../../_suite/primitives";

function CampaignTimelinePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.adverts.listCampaigns(),
    []
  );
  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.adverts.campaigns" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          Flight timeline — each row is a flight window with creative sets.
        </p>
      }
    >
      <AsyncStateBoundary loading={loading} error={error}>
        <div className="position-relative ps-3 border-start border-primary">
          {(data || []).map(c => (
            <Card key={c.id} className="mb-4 ms-3 border-0 shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between flex-wrap gap-2">
                  <div>
                    <h5 className="mb-1">{c.name}</h5>
                    <Badge
                      color={c.status === "active" ? "success" : "secondary"}
                    >
                      {c.status}
                    </Badge>
                  </div>
                  <div className="text-end">
                    <div className="small text-muted">Spend</div>
                    <div className="h5 mb-0">${c.spend}</div>
                    <div className="small">ROAS {c.roas}x</div>
                  </div>
                </div>
                <Row className="mt-3 g-2">
                  {(c.creatives || [{ id: "stub", format: "Creative", ctr: 0 }]).map(
                    (cr, idx) => (
                      <Col xs="6" md="4" key={cr.id || idx}>
                        <div className="bg-light rounded p-3 text-center small border h-100">
                          <div className="text-muted">{cr.format}</div>
                          <div className="font-weight-bold mt-1">
                            CTR {cr.ctr}%
                          </div>
                        </div>
                      </Col>
                    )
                  )}
                </Row>
                <Button
                  tag={Link}
                  color="primary"
                  size="sm"
                  className="mt-3"
                  to={c.id}
                  relative="path"
                >
                  Flight detail
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function CampaignDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.adverts.getCampaign(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.adverts.campaign-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col md="8">
              <h2>{data.name}</h2>
              <p>
                <Badge>{data.status}</Badge>
              </p>
              <Card className="mb-3">
                <CardBody>
                  <CardTitle>Placement performance (stub)</CardTitle>
                  <div
                    className="bg-light rounded d-flex align-items-end justify-content-around p-3"
                    style={SUITE_ADVERTS_BAR_SHELL_160}
                  >
                    {ADVERTS_PLACEMENT_DEMO_BAR_STYLES.map((barStyle, i) => (
                      <div
                        key={i}
                        className="bg-primary rounded-top"
                        style={barStyle}
                      />
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md="4">
              <Card>
                <CardBody>
                  <p>Spend ${data.spend}</p>
                  <p>ROAS {data.roas}x</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

type AdvertsReportSummary = {
  impressions: number;
  clicks: number;
  spend: number;
  roas: number;
};

type AdvertsBilling = {
  balance: number;
  nextInvoice: string;
  paymentMethod: string;
};

function AdvertsReportsChartPage() {
  const [summary, setSummary] = React.useState<AdvertsReportSummary | null>(
    null
  );
  const [slice, setSlice] = useState("7d");
  React.useEffect(() => {
    suiteApi.adverts.getReportSummary().then(setSummary);
  }, []);
  const prior = summary
    ? {
        impressions: Math.round(summary.impressions * 0.88),
        clicks: Math.round(summary.clicks * 0.91),
        spend: Math.round(summary.spend * 0.94)
      }
    : null;
  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.adverts.reports" />}
      afterBreadcrumb={
        <div className="d-flex flex-wrap gap-2 mb-0">
          {["7d", "28d", "qtd"].map(k => (
            <Button
              key={k}
              size="sm"
              color={slice === k ? "primary" : "light"}
              onClick={() => setSlice(k)}
            >
              {k}
            </Button>
          ))}
        </div>
      }
    >
      <KpiStatCards
        items={[
          {
            title: "Impressions",
            value: summary ? summary.impressions : "—",
            sub: prior
              ? `vs prior slice: ${prior.impressions.toLocaleString()}`
              : undefined
          },
          {
            title: "Clicks",
            value: summary ? summary.clicks : "—",
            sub: prior ? `prior: ${prior.clicks.toLocaleString()}` : undefined
          },
          {
            title: "Spend / ROAS",
            value: summary ? `$${summary.spend} · ${summary.roas}x` : "—",
            sub: prior ? `prior spend $${prior.spend}` : undefined
          }
        ]}
      />
      <Card>
        <CardBody>
          <CardTitle tag="h5">Trend</CardTitle>
          <div
            className="bg-light rounded text-muted small d-flex align-items-center justify-content-center"
            style={SUITE_ADVERTS_TREND_PLACEHOLDER_220}
          >
            Connect time-series from your ads API (slice: {slice})
          </div>
        </CardBody>
      </Card>
    </SuitePageShell>
  );
}

function AdvertsBillingInvoicePage() {
  const [b, setB] = React.useState<AdvertsBilling | null>(null);
  React.useEffect(() => {
    suiteApi.adverts.getBilling().then(setB);
  }, []);
  return (
    <SuitePageShell heading={<IntlMessages id="suite.adverts.billing" />}>
      <Card className="border-0 shadow-lg">
        <CardBody className="p-5">
          <Row className="mb-4">
            <Col>
              <h2 className="text-primary">Statement</h2>
              <p className="text-muted mb-0">Ad platform billing (demo)</p>
            </Col>
            <Col className="text-end">
              <Badge color="success">Current</Badge>
            </Col>
          </Row>
          {b && (
            <>
              <table className="table">
                <tbody>
                  <tr>
                    <td>Account balance</td>
                    <td className="text-end font-weight-bold">${b.balance}</td>
                  </tr>
                  <tr>
                    <td>Next invoice</td>
                    <td className="text-end">{b.nextInvoice}</td>
                  </tr>
                  <tr>
                    <td>Payment method</td>
                    <td className="text-end">{b.paymentMethod}</td>
                  </tr>
                </tbody>
              </table>
              <Button color="primary">Download PDF (wire)</Button>
            </>
          )}
        </CardBody>
      </Card>
    </SuitePageShell>
  );
}

export function AdvertsSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="adverts" />}>
        <Route index element={<AdvertsDashboard />} />
        <Route path="campaigns" element={<CampaignTimelinePage />} />
        <Route path="campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="reports" element={<AdvertsReportsChartPage />} />
        <Route path="billing" element={<AdvertsBillingInvoicePage />} />
      </Route>
    </Routes>
  );
}
